
import Component from "vue-class-component"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import PageHeader from "@/superannuation/PageHeader.vue"
import {Company, Member, Plan, PlanService, Platform} from "@/superannuation/PlanService"
import SelectDropDown from "@/components/SelectDropDown.vue"
import ErrorMessage from "@/components/ErrorMessage.vue"
import {restDownloadFile} from "@/utils/rest"
import {theme} from "@/theme"
import Axios, {AxiosError, AxiosResponse} from "axios"
import {Inject} from "vue-property-decorator"
import {AuthenticationService} from "@/AuthenticationService"
import {format} from "date-fns"
import PopupDate from "@/components/PopupDate.vue"
import {getIsoDate} from "@/utils/formatter"

interface PcrPdf {
  id: string
  generated: string
  start: string
  end: string
  template: string
}

@Component({
  components: {
    ErrorMessage,
    SelectDropDown,
    PageHeader,
    HelpIcon,
    PopupDate,
  },
})
export default class ReportsView extends Vue {
  @Inject()
  authenticationService: AuthenticationService

  @Inject()
  planService: PlanService

  t = theme()

  readonly visibleColumns = ["id", "generated", "start", "end", "template"]

  $refs: {
    platformSelect: SelectDropDown
    planSelect: SelectDropDown
    memberSelect: SelectDropDown
    companySelect: SelectDropDown
  }

  platform: Platform | null = null
  plan: Plan | null = null
  member: Member | null = null
  company: Company | null = null
  startDate: string | null = null
  endDate: string | null = null
  pcrPdfs: Array<PcrPdf> = new Array<PcrPdf>()

  planReportInProgress = false
  newEmployeeFlyerInProgress = false
  emailDeliveryInProgress = false
  digitalEngagementReportInProgress = false

  message: string | null = null

  dataInProgress = false

  timer: NodeJS.Timeout | null = null

  isAdmin = false

  async created() {
    this.isAdmin = await this.authenticationService.isAdmin()
  }

  async destroyed() {
    clearInterval(this.timer!)
  }

  get isPlanReportingDisabled(): boolean {
    return this.plan == null
  }

  get isMemberReportingDisabled(): boolean {
    return this.member == null
  }

  get isCompanyReportingDisabled(): boolean {
    return this.company === null
  }

  get isPolicyCommitteeReportingDisabled(): boolean {
    return this.isPlanReportingDisabled || this.isCompanyReportingDisabled
  }

  get isSuperScoreClient(): boolean {
    return this.company!.plan.superScoreClient
  }

  get isChecklistReportingDisabled(): boolean {
    return this.isCompanyReportingDisabled || !this.isSuperScoreClient
  }

  get superscoreReportLink() {
    return this.plan == null
      ? ""
      : `/reports/plan-superscore-report/?planId=${this.plan.id}&viewMode=book`
  }

  get investmentAdviceLink() {
    return this.member == null
      ? ""
      : `/reports/intra-fund-investment-report/?memberId=${this.member.id}`
  }

  private async downloadInvestmentAdviceReport() {
    if (this.member != null) {
      this.planReportInProgress = true
      await restDownloadFile(
        `/pdf/intra-fund-investment-report/?memberId=${this.member.id}`,
        `${this.member.surname} - InvestmentAdvice.pdf`
      )
        .then(() => {
          this.message = null
        })
        .catch(async (reason: AxiosError<any>) => {
          console.error(reason)
          this.message = JSON.parse(await reason.response!.data.text()).message
        })
        .finally(() => (this.planReportInProgress = false))
    }
  }

  get insuranceAdviceLink() {
    return this.member == null ? "" : `/reports/limited-advice-report/?memberId=${this.member.id}`
  }

  get pcrPreviewLink() {
    return this.isPolicyCommitteeReportingDisabled
      ? ""
      : `/policyCommitteeReport?planId=${this.plan!.id}&companyId=${
          this.company!.id
        }&preview=true&engagedOnly=${String(this.planService.engagedOnly)}`
  }

  get planId() {
    return this.plan?.id
  }

  get platformId() {
    return this.platform?.id
  }

  private platformSelected(platform: Platform | null) {
    this.platform = platform
    if (platform != null) {
      this.$refs.planSelect.model = null
      this.plan = null
      this.$refs.memberSelect.model = null
      this.member = null
      this.$refs.companySelect.model = null
      this.company = null
    }
  }

  private planSelected(plan: Plan | null) {
    this.plan = plan
    if (plan != null) {
      this.$refs.memberSelect.model = null
      this.member = null
      this.$refs.companySelect.model = null
      this.company = null
    }
  }

  private companySelected(company: Company | null) {
    this.company = company
    if (company != null) {
      this.$refs.memberSelect.model = null
      this.member = null
      void this.getPcrPdfs()
    }
  }

  private memberSelected(member: Member | null) {
    this.member = member
  }

  private memberLabel(): (option: Member) => string {
    return (option: Member) => `${option.surname} [${option.login}] - ${option.companyName}`
  }

  private async refresh(done: () => void) {
    await this.getPcrPdfs()
    done()
  }

  private async downloadPlanReport() {
    if (this.plan != null) {
      this.planReportInProgress = true
      await restDownloadFile(
        `/api/plan/${this.plan.id}/superScoreDetails`,
        `${this.plan.name} - SuperScore details.csv`
      )
        .then(() => {
          this.message = null
        })
        .catch(async (reason: AxiosError<any>) => {
          this.message = JSON.parse(await reason.response!.data.text()).message
        })
        .finally(() => (this.planReportInProgress = false))
    }
  }

  private async downloadEventsReport() {
    this.planReportInProgress = true
    const startDate = getIsoDate(this.startDate)
    const endDate = getIsoDate(this.endDate)
    const now = format(new Date(), "ddMMyyyy")
    await restDownloadFile(`/api/members/eventsCsv`, `Member events -${now}.csv`, {
      startDate,
      endDate,
    })
      .then(() => {
        this.message = null
      })
      .catch(async (reason: AxiosError<any>) => {
        this.message = JSON.parse(await reason.response!.data.text()).message
      })
      .finally(() => (this.planReportInProgress = false))
  }

  private async downloadNewEmployeeFlyer() {
    if (this.company != null) {
      this.newEmployeeFlyerInProgress = true
      await restDownloadFile(
        `/api/companies/${this.company.id}/newFlyer`,
        `${this.company.name} - New Employee.pdf`
      )
        .then(() => {
          this.message = null
        })
        .catch(async (reason: AxiosError<any>) => {
          this.message = JSON.parse(await reason.response!.data.text()).message
        })
        .finally(() => (this.newEmployeeFlyerInProgress = false))
    }
  }

  private async downloadSuperScoreChecklist() {
    if (!this.isChecklistReportingDisabled) {
      const now = format(new Date(), "ddMMyyyy")
      await restDownloadFile(
        `/api/companies/${this.company!.id}/superScoreChecklist`,
        `${this.company!.name} - SuperScoreChecklist-${now}.docx`
      )
        .then(() => (this.message = null))
        .catch(async (reason: AxiosError<any>) => {
          this.message = JSON.parse(await reason.response!.data.text()).message
        })
    }
  }

  private async downloadMoneyFitDetails() {
    this.digitalEngagementReportInProgress = true
    const path = "/api/moneyFit/exportDetailsCsv"
    const fullPath = this.company === null ? path : `${path}?companyId=${this.company.id}`
    const now = format(new Date(), "ddMMyyyy")

    await restDownloadFile(fullPath, `MoneyFitQuestionnaire-${now}.csv`)
      .then(() => (this.message = null))
      .catch(async (reason: AxiosError<any>) => {
        this.message = JSON.parse(await reason.response!.data.text()).message
      })
      .finally(() => (this.digitalEngagementReportInProgress = false))
  }

  private async downloadInsuranceNeedsQuote() {
    this.digitalEngagementReportInProgress = true
    const path = "/api/insuranceCalculation/exportNeedsQuoteCsv"
    const now = format(new Date(), "ddMMyyyy")
    const fullPath = this.platformId === undefined ? path : `${path}?platformId=${this.platformId}`

    await restDownloadFile(fullPath, `InsuranceNeedsQuote-${now}.csv`)
      .then(() => (this.message = null))
      .catch(async (reason: AxiosError<any>) => {
        this.message = JSON.parse(await reason.response!.data.text()).message
      })
      .finally(() => (this.digitalEngagementReportInProgress = false))
  }

  private async downloadEmailDelivery() {
    this.emailDeliveryInProgress = true
    const path = "/api/devops/monitors/email"
    const fullPath = this.planId === undefined ? path : `${path}?planId=${this.planId}`
    await restDownloadFile(fullPath, `EmailDelivery.csv`)
      .then(() => {
        this.message = null
      })
      .catch(async (reason: AxiosError<any>) => {
        this.message = JSON.parse(await reason.response!.data.text()).message
      })
      .finally(() => (this.emailDeliveryInProgress = false))
  }

  private openPolicyCommitteeReport() {
    if (!this.isPolicyCommitteeReportingDisabled) {
      const url = `/playwright/pcr/pdf/${this.plan!.id}?companyId=${this.company!.id}`
      window.open(url, "_blank")
    }
  }

  private setIntervalAttempts(
    callback: () => Promise<void>,
    timeout: number,
    attempts: number
  ): NodeJS.Timeout {
    let x = 0
    const timer = setInterval(() => {
      void callback()
      if (++x === attempts) {
        window.clearInterval(timer)
      }
    }, timeout)
    return timer
  }

  private async requestPolicyCommitteeReport() {
    if (!this.isPolicyCommitteeReportingDisabled) {
      this.dataInProgress = true
      const form = new FormData()
      form.set("companyId", this.company!.id)
      form.set("engagedOnly", String(this.planService.engagedOnly))
      await Axios.post(`/api/plans/${this.plan!.id}/reports/request`, form)
        .then(() => {
          this.message = null
          this.timer = this.setIntervalAttempts(this.getPcrPdfs, 15000, 10)
        })
        .catch((reason: AxiosError<any>) => {
          this.message = reason.response?.data.message
        })
        .finally(() => (this.dataInProgress = false))
    }
  }

  private async getPcrPdfs() {
    if (this.plan != null && this.company != null) {
      this.dataInProgress = true
      await Axios.get(`/api/plans/${this.plan.id}/reports/result`, {
        params: {companyId: this.company.id},
      })
        .then((response: AxiosResponse) => {
          this.message = null
          this.pcrPdfs = response.data
          if (this.pcrPdfs.length > 0) clearInterval(this.timer!)
        })
        .catch((reason: AxiosError<any>) => {
          this.message = reason.response?.data.message
        })
        .finally(() => (this.dataInProgress = false))
    }
  }

  private async downloadPcrPdf(pcrPdf: PcrPdf) {
    if (this.plan != null) {
      await restDownloadFile(
        `/api/plans/${this.plan.id}/reports/result`,
        `${this.plan.name} - Policy Committee Report.pdf`,
        {resultId: pcrPdf.id}
      )
        .then(() => {
          this.message = null
        })
        .catch(async (reason: AxiosError<any>) => {
          this.message = JSON.parse(await reason.response!.data.text()).message
        })
    }
  }
}
